import { motion } from "framer-motion";
import {
  Receipt,
  BookOpenCheck,
  Boxes,
  Users,
  LineChart,
  Landmark,
  UsersRound,
  Wifi,
  Cloud,
  Lock,
  Settings,
} from "lucide-react";
import { fadeUp, stagger } from "@/lib/motion";

const FEATURES = [
  { Icon: Receipt, title: "GST & e-Invoicing", desc: "Auto GSTR-1, GSTR-3B, e-Way Bills." },
  { Icon: BookOpenCheck, title: "Accounting", desc: "Ledgers, vouchers & double-entry books." },
  { Icon: Boxes, title: "Inventory", desc: "Multi-godown, batches, expiry, reorder." },
  { Icon: Users, title: "Payroll", desc: "PF, ESI, TDS with statutory reports." },
  { Icon: LineChart, title: "Reports", desc: "P&L, balance sheet, MIS in one click." },
  { Icon: Landmark, title: "Banking", desc: "Bank reconciliation & payment integrations." },
  { Icon: UsersRound, title: "Multi-user", desc: "Role-based access across teams." },
  { Icon: Wifi, title: "Remote Access", desc: "Access Tally securely from anywhere." },
  { Icon: Cloud, title: "Cloud Ready", desc: "Deploy TallyPrime on cloud servers." },
  { Icon: Lock, title: "Security", desc: "Audit trail, data vault & user controls." },
  { Icon: Settings, title: "Tally Customization", desc: "TDL add-ons, custom reports & workflows." },
];

export default function Features() {
  return (
    <section id="features" data-testid="features-section" className="relative py-24 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          variants={stagger(0.1, 0.08)}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
          className="max-w-2xl"
        >
          <motion.span variants={fadeUp} className="eyebrow"><span className="dot" /> Features</motion.span>
          <motion.h2 variants={fadeUp} className="mt-6 font-display font-black text-4xl sm:text-5xl leading-[1.08] tracking-tight text-[#1A1A2E]">
            Every module your{" "}
            <span className="font-serif-accent text-[#0D47A1]">business needs.</span>
          </motion.h2>
          <motion.p variants={fadeUp} className="mt-5 text-[17px] text-[#4A5568] max-w-xl leading-relaxed">
            One platform, ten superpowers — from billing to banking to compliance.
          </motion.p>
        </motion.div>

        <motion.div
          variants={stagger(0.03, 0.05)}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.15 }}
          className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4"
        >
          {FEATURES.map(({ Icon, title, desc }, i) => (
            <motion.div
              key={title}
              variants={fadeUp}
              whileHover={{ y: -4 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              data-testid={`feature-${i}`}
              className="group relative rounded-2xl bg-[#FAF9F6] border border-[#0D47A1]/8 p-5 hover:border-[#FFA000]/40 transition-all duration-400"
            >
              <div className="w-12 h-12 rounded-xl bg-[#0D47A1]/8 flex items-center justify-center mb-4 group-hover:bg-[#0D47A1] transition-colors duration-400" style={{ backgroundColor: "rgba(13,71,161,0.08)" }}>
                <Icon className="w-5 h-5 text-[#0D47A1] group-hover:text-white transition-colors duration-400" strokeWidth={1.75} />
              </div>
              <h4 className="font-display font-bold text-[15.5px] text-[#1A1A2E] tracking-tight">{title}</h4>
              <p className="mt-1 text-[13px] text-[#4A5568] leading-relaxed">{desc}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
